import { useMemo } from 'react';
import { Permission } from '@metron/shared';
import { useCustomers } from '../../hooks/useCustomers.ts';
import { usePermissions } from '../../hooks/usePermissions.ts';
import { useTableUrlState } from '../../hooks/useTableUrlState.ts';
import { useUsers } from '../../hooks/useUsers.ts';
import {
  USER_STATUS_OPTIONS,
  type UserStatusKey,
  type UserStatusOption,
} from '../../lib/user-status.ts';
import { USERS_TABLE_DEFAULT_SORTING } from './columns.tsx';
import { belongsTo, type CustomerNameEntry, toRow } from './derive.ts';
import { buildCustomerOptions, hasActiveFilters, matchesFilters } from './filter.ts';
import {
  USERS_TABLE_URL_PREFIX,
  useStableScope,
  useUsersTableFilters,
} from './useUsersTableFilters.ts';
import type {
  RoleState,
  RoleStateKind,
  UsersTableFilters,
  UsersTableRow,
  UsersTableScope,
  UserTypeState,
} from './types.ts';
import type { UsersTablePermissions } from './UserRowActions.tsx';

/**
 * Module-level so the reference is stable: `useTableUrlState` memoises on it.
 * Mutable copy of the readonly constant, which is the shape TanStack wants.
 */
const DEFAULT_SORTING = USERS_TABLE_DEFAULT_SORTING.map((entry) => ({ ...entry }));

/**
 * The Type filter's vocabulary, in the Type column's own order.
 *
 * "Unknown" belongs to the vocabulary: a row whose Cognito group lookup failed
 * renders "Unknown" in that column, and those rows have to be reachable, not
 * merely visible — finding them is how the failure gets acted on. It is only
 * *offered* when such a row exists, which is what makes its presence a signal.
 */
const TYPE_STATES: readonly UserTypeState[] = ['metron', 'external', 'unknown'];

/**
 * The Role filter's vocabulary, in the Role column's own order — "Unknown"
 * included, for the same reason as in Type.
 *
 * Labels come from `roleLabel`, the same function the Role cell uses, so an
 * option can never read differently from the cells it selects. `propertyCount`
 * is irrelevant to the option label and is only here to satisfy the state
 * shape.
 */
const ROLE_STATES: readonly RoleState[] = [
  { kind: 'admin', propertyCount: 0 },
  { kind: 'propertyManager', propertyCount: 0 },
  { kind: 'metronInternal' },
  { kind: 'notAssigned' },
  { kind: 'none' },
  { kind: 'unknown' },
];

/** The Type, Role and Status dropdown contents, each in canonical order. */
export interface UsersTableStateOptions {
  types: readonly UserTypeState[];
  roles: readonly RoleState[];
  statuses: readonly UserStatusOption[];
}

/**
 * The Type, Role and Status options: the states actually present in `rows`,
 * each list kept in its own fixed canonical order. Built from rows rather than
 * from the vocabulary — the same way `buildCustomerOptions` builds the customer
 * list — so an option that no row can match is never offered. Nothing leaves
 * the vocabulary: the URL codecs still accept every value.
 *
 * The currently selected value is kept even when no row has it. A URL carrying
 * a stale filter (`usersType=unknown` after the failure cleared) would
 * otherwise leave the control blank while the URL kept filtering, with no way
 * to see or undo it; keeping the option shows the honest empty result with the
 * control and the URL in agreement.
 */
function buildStateOptions(
  rows: readonly UsersTableRow[],
  filters: UsersTableFilters,
): UsersTableStateOptions {
  const types = new Set<UserTypeState>();
  const roles = new Set<RoleStateKind>();
  const statuses = new Set<UserStatusKey>();

  for (const row of rows) {
    types.add(row.type);
    roles.add(row.roleState.kind);
    statuses.add(row.status.key);
  }

  if (filters.type !== null) types.add(filters.type);
  if (filters.role !== null) roles.add(filters.role);
  if (filters.status !== null) statuses.add(filters.status);

  return {
    types: TYPE_STATES.filter((type) => types.has(type)),
    roles: ROLE_STATES.filter((state) => roles.has(state.kind)),
    statuses: USER_STATUS_OPTIONS.filter((option) => statuses.has(option.key)),
  };
}

/**
 * Everything `UsersTable` needs, composed in one place: the two queries, the
 * permission flags, the URL-held table state and the client-side pipeline.
 *
 * The pipeline, in order:
 *
 * ```
 * users            (['users','with-customers'])
 *   → rows         = users.map(toRow), minus end consumers — derive once, per user
 *   → scoped       = customer mode ? rows.filter(belongsTo) : rows
 *   → visible      = scoped.filter(matchesFilters)
 *   → DataTable                                            — sorts and paginates from the URL
 * ```
 *
 * Filtering stays entirely client-side, exactly as both existing tables do
 * today; the endpoint already returns the whole pool in one response.
 */
export function useUsersTable(scope: UsersTableScope) {
  const stableScope = useStableScope(scope);

  const {
    users,
    isLoading: usersLoading,
    error,
    createUserWithoutInvalidation,
    updateUserWithoutInvalidation,
    deleteUser,
    permanentDeleteUser,
    resetPassword,
    regenerateTempPassword,
    resendWelcomeEmail,
  } = useUsers();

  // Customer *names* for property users come from this query, so rendering
  // before it settles would flash a resolvable customer as its bare id.
  const { customers, isLoading: customersLoading } = useCustomers();
  const { hasPermission } = usePermissions();

  const table = useTableUrlState({
    prefix: USERS_TABLE_URL_PREFIX,
    defaultSorting: DEFAULT_SORTING,
  });
  const { filters, setFilters, clear } = useUsersTableFilters(stableScope);

  const customersById = useMemo(
    () =>
      new Map<number, CustomerNameEntry>(
        customers.map((customer) => [
          customer.id,
          { id: customer.id, name: customer.waterscopeAccountName },
        ]),
      ),
    [customers],
  );

  // End consumers (My WaterScope tenants) never appear here; they are listed
  // on each customer's Consumers tab instead (MET-3778).
  const rows = useMemo(
    () => users.map((user) => toRow(user, customersById)).filter((row) => !row.isEndConsumer),
    [users, customersById],
  );

  const scoped = useMemo(
    () =>
      stableScope.mode === 'customer'
        ? rows.filter((row) => belongsTo(row, stableScope.customerId))
        : rows,
    [rows, stableScope],
  );

  const visible = useMemo(
    () => scoped.filter((row) => matchesFilters(row, filters)),
    [scoped, filters],
  );

  // Built from the rows actually in scope, so a customer reachable only through
  // a property user appears in the dropdown instead of being unselectable.
  const customerOptions = useMemo(() => buildCustomerOptions(scoped), [scoped]);

  // The Type/Role/Status option lists are built from the rows in scope —
  // exactly the rows the table shows with no value filter set. The value
  // filters themselves are deliberately *not* applied, so choosing one option
  // can never empty the other two dropdowns.
  const stateOptions = useMemo(() => buildStateOptions(scoped, filters), [scoped, filters]);

  // Resolved to primitives first: `usePermissions` returns a fresh
  // `hasPermission` on every render, so memoising on the function itself would
  // rebuild the column defs on every render.
  const canCreate = hasPermission(Permission.CREATE_USER);
  const canUpdate = hasPermission(Permission.UPDATE_USER);
  const canResetPassword = hasPermission(Permission.RESET_PASSWORD);
  const canRegenerateTempPassword = hasPermission(Permission.REGENERATE_TEMP_PASSWORD);
  const canDelete = hasPermission(Permission.DELETE_USER);

  const can = useMemo<UsersTablePermissions>(() => ({
    create: canCreate,
    update: canUpdate,
    resetPassword: canResetPassword,
    regenerateTempPassword: canRegenerateTempPassword,
    delete: canDelete,
  }), [canCreate, canUpdate, canResetPassword, canRegenerateTempPassword, canDelete]);

  return {
    scope: stableScope,
    rows: visible,
    /** Rows in scope before any filter — drives "there are no users at all". */
    scopedCount: scoped.length,
    customerOptions,
    /** Type/Role/Status options: only the states present in `optionRows`. */
    stateOptions,
    isLoading: usersLoading || customersLoading,
    error,
    table,
    filters,
    setFilters,
    clear,
    isDirty: hasActiveFilters(filters),
    can,
    writes: {
      createUserWithoutInvalidation,
      updateUserWithoutInvalidation,
      deleteUser,
      permanentDeleteUser,
      resetPassword,
      regenerateTempPassword,
      resendWelcomeEmail,
    },
  };
}
